import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { Box, Stack, SvgIcon } from '@mui/material';

import ChatbotIcon from 'src/assets/chatbot/chatbot.svg?react';
import type { ChatMessage } from 'src/types/chatbot';
import { CHATBOT_ROLE } from 'src/components/chatbot/constants';

import { sanitizeHTML } from './helpers';

import { styles } from './index.styles';

export const CHATBOT_ICON_TEST_ID = 'chatbot-icon-test-id';

interface ChatMessageProps extends Omit<ChatMessage, 'content'> {
  content: string | ReactNode;
}

export const MessageDisplay: FC<ChatMessageProps> = ({
  content,
  displayMessage,
  role,
  highlight,
}) => {
  const sanitizedMessage = useMemo(() => {
    if (role !== CHATBOT_ROLE) {
      return undefined;
    }

    if (displayMessage) return sanitizeHTML(displayMessage);
    if (typeof content === 'string') return sanitizeHTML(content);

    return undefined;
  }, [content, displayMessage, role]);

  return (
    <Stack direction="row" sx={{ pt: displayMessage ? 1 : 4 }}>
      {role === CHATBOT_ROLE ? (
        <>
          {!displayMessage && (
            <Stack direction="column" sx={styles.iconBot}>
              <SvgIcon
                component={ChatbotIcon}
                data-test-id={CHATBOT_ICON_TEST_ID}
                inheritViewBox
              />
            </Stack>
          )}
          <Box
            sx={[
              displayMessage ? styles.textBot : styles.textBubbleBot,
              highlight ? styles.highlight : {},
            ]}
            {...(sanitizedMessage
              ? { dangerouslySetInnerHTML: { __html: sanitizedMessage } }
              : { children: content })}
          />
        </>
      ) : (
        <Box
          sx={[
            displayMessage ? styles.textBot : styles.textBubbleBot,
            highlight ? styles.highlight : {},
          ]}
        >
          {displayMessage || content}
        </Box>
      )}
    </Stack>
  );
};

export default MessageDisplay;
